import React from 'react';
import PropTypes from 'prop-types';
import { ProgressCircular } from '@americanexpress/dls-react';

/**
 * ProgressCircularDls renders a DLS ProgressCircle spinner/indicator.
 *
 * Visibility is driven by a key in formSubmissionPayload:
 *   - When formSubmissionPayload[loadingKey] is truthy  → spinner is shown
 *   - When formSubmissionPayload[loadingKey] is falsy   → spinner is hidden
 *
 * JSON config example:
 * {
 *   "fieldType": "progressCircularDls",
 *   "fieldId": "mySpinner",
 *   "cssProperties": { "style": "flex flex-justify-center pad-2" },
 *   "loadingKey": "isLoading",
 *   "size": "lg",
 *   "determinate": false,
 *   "label": "Loading, please wait",
 *   "value": 0,
 *   "percent": false
 * }
 */
const ProgressCircularDls = (props) => {
  const {
    cssProperties,
    loadingKey,
    formSubmissionPayload = {},
    size,
    determinate = false,
    label,
    value = 0,
    percent = false,
    theme = {},
  } = props;

  const isLoading = loadingKey ? !!formSubmissionPayload[loadingKey] : true;

  if (!isLoading) {
    return null;
  }

  return (
    <div className={cssProperties?.style}>
      <ProgressCircular
        size={size}
        determinate={determinate}
        value={value}
        percent={percent}
        label={label}
        theme={theme}
      />
    </div>
  );
};

ProgressCircularDls.propTypes = {
  cssProperties: PropTypes.shape({
    style: PropTypes.string,
  }),
  /** Key in formSubmissionPayload that controls visibility (truthy = show, falsy = hide) */
  loadingKey: PropTypes.string,
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  /** Spinner size: 'sm' or 'lg' */
  size: PropTypes.oneOf(['sm', 'lg']),
  /** determinate (true) shows a value-based circle; false = indeterminate spinner */
  // eslint-disable-next-line react/boolean-prop-naming -- prop name matches DLS API, cannot rename
  determinate: PropTypes.bool,
  /** Aria label for the progressbar */
  label: PropTypes.string,
  /** Current progress value 0-100 (only used when determinate=true) */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Show percent symbol (determinate mode only) */
  // eslint-disable-next-line react/boolean-prop-naming -- prop name matches DLS API, cannot rename
  percent: PropTypes.bool,
  /** Emotion theme override object */
  theme: PropTypes.objectOf(PropTypes.shape),
};

export default ProgressCircularDls;
